import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Upload, Loader2, ArrowLeft, Check, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

// Excel Toplu Sınıflandırma + Kategori (FAZ 1+2) — export + ÖNİZLEME → onay → uygula.
// Kapsam: yalnız `classification` + `category_id`. Silme/stok/yeni ürün/BH YOK.
// Eşleştirme: product_id → source_id → SKU (isimle otomatik eşleştirme yok).
const COUNT_LABELS = [
  ["eslesen", "Eşleşen", "text-emerald-400 border-emerald-500/30"],
  ["urun_tipi", "Ürün Tipi", "text-fuchsia-400 border-fuchsia-500/30"],
  ["aktif", "Aktif/Pasif", "text-lime-400 border-lime-500/30"],
  ["ticari", "Ticari", "text-sky-400 border-sky-500/30"],
  ["uretim", "Üretim", "text-violet-400 border-violet-500/30"],
  ["kumas", "Kumaş", "text-amber-400 border-amber-500/30"],
  ["aksesuar", "Aksesuar", "text-pink-400 border-pink-500/30"],
  ["gereksiz", "Gereksiz", "text-slate-400 border-slate-500/30"],
  ["kategori", "Kategori", "text-teal-400 border-teal-500/30"],
  ["fiyat", "Fiyat", "text-cyan-400 border-cyan-500/30"],
  ["para_birimi", "P. Birimi", "text-indigo-400 border-indigo-500/30"],
  ["stok", "Stok", "text-yellow-300 border-yellow-500/30"],
  ["liste_alis", "Liste Alış", "text-orange-300 border-orange-500/30"],
  ["alis_iskonto", "Alış İskonto", "text-rose-300 border-rose-400/30"],
  ["satis_artis", "Satış Artış %", "text-green-300 border-green-500/30"],
  ["hatali", "Hatalı", "text-rose-400 border-rose-500/30"],
  ["eslesmeyen", "Eşleşmeyen", "text-orange-400 border-orange-500/30"],
];

const FIELD_LABELS = {
  siniflandirma: "Sınıflandırma", kategori: "Kategori", aktif: "Aktif/Pasif",
  satis_fiyati: "Satış Fiyatı", para_birimi: "Para Birimi", stok: "Stok",
  urun_tipi: "Ürün Tipi",
  liste_alis_fiyati: "Liste Alış Fiyatı", alis_iskonto_orani: "Alış İskonto %",
  net_alis_fiyati: "Net Alış (bilgi)",
  satis_artis_orani: "Satış Artış %", onerilen_satis: "Önerilen Satış (bilgi)",
};

export default function BulkClassifyDialog({ open, onOpenChange, onDone }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  // Export filtresi: Tüm Ürünler / Markaya Göre / Kategoriye Göre
  const [mode, setMode] = useState("all");
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [brandSel, setBrandSel] = useState("");
  const [catSel, setCatSel] = useState("");

  useEffect(() => {
    if (!open) return;
    setMode("all"); setBrandSel(""); setCatSel("");
    api.get("/products/brands").then(({ data }) => setBrands(data || [])).catch(() => {});
    api.get("/product-categories").then(({ data }) => setCats((data || []).filter((c) => c.active))).catch(() => {});
  }, [open]);

  const close = (v) => {
    onOpenChange?.(v);
    if (!v) { setPreview(null); setFile(null); }
  };

  const doExport = async () => {
    if (mode === "brand" && !brandSel) return toast.error("Marka seçin");
    if (mode === "category" && !catSel) return toast.error("Kategori seçin");
    try {
      setBusy(true);
      const params = {};
      if (mode === "brand") params.brand = brandSel;
      if (mode === "category") params.category_id = catSel;
      const resp = await api.get("/products/classify/export", { responseType: "blob", params });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "urun_siniflandirma.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Ürün listesi Excel olarak indirildi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doPreview = async (f) => {
    if (!f) return;
    try {
      setBusy(true);
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/products/classify/preview", fd);
      setFile(f); setPreview(data);
      if (!data.degisecek) toast.info("Değişecek kayıt yok");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const doApply = async () => {
    if (!file) return;
    try {
      setBusy(true);
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/products/classify/apply", fd);
      toast.success(
        `${data.applied} ürün güncellendi` +
        (data.stock_applied ? ` · ${data.stock_applied} stok düzeltme hareketi` : "")
      );
      close(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const degisecekUrun = preview ? (preview.degisecek_urun ?? preview.degisecek) : 0;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-3xl max-h-[90vh] flex flex-col p-0" data-testid="classify-dialog">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-cyan-400" /> Excel Toplu Ürün Yönetimi
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 px-6 pb-6 space-y-4">
          {!preview && (
            <div className="space-y-4">
              <div className="rounded-sm border border-slate-800 bg-slate-950/40 p-4 space-y-2">
                <div className="text-xs text-slate-300 font-semibold">1. Şablonu indir</div>
                <div className="flex flex-wrap gap-1.5" data-testid="export-filter-modes">
                  {[["all", "Tüm Ürünler"], ["brand", "Markaya Göre"], ["category", "Kategoriye Göre"]].map(([k, label]) => (
                    <button key={k} type="button" data-testid={`export-mode-${k}`}
                      onClick={() => setMode(k)}
                      className={`px-2.5 py-1 text-[11px] rounded-sm border transition-colors ${
                        mode === k ? "border-cyan-500 bg-cyan-950/40 text-cyan-300"
                                   : "border-slate-700 bg-transparent text-slate-400 hover:border-slate-600"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                {mode === "brand" && (
                  <select data-testid="export-brand-select" value={brandSel} onChange={(e) => setBrandSel(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-700 rounded-sm h-8 text-xs text-slate-200 px-2">
                    <option value="">Marka seçin…</option>
                    {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                )}
                {mode === "category" && (
                  <select data-testid="export-category-select" value={catSel} onChange={(e) => setCatSel(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-700 rounded-sm h-8 text-xs text-slate-200 px-2">
                    <option value="">Kategori seçin…</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                )}
                <div className="text-[11px] text-slate-500">
                  Seçilen kapsamdaki ürünler; product_id, source_id, marka, ürün adı, SKU, tip, sınıflandırma, kategori, fiyat ve stok kolonlarıyla iner.
                </div>
                <Button onClick={doExport} disabled={busy} data-testid="classify-download-btn"
                  className="bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-sm text-xs uppercase tracking-wider">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> Excel'e Aktar</>}
                </Button>
              </div>
              <div className="rounded-sm border border-slate-800 bg-slate-950/40 p-4 space-y-2">
                <div className="text-xs text-slate-300 font-semibold">2. Düzenlenecek kolonları doldur ve yükle</div>
                <div className="text-[11px] text-slate-500">
                  Sınıflandırma, Kategori, Aktif/Pasif ve Para Birimi kolonlarında hazır seçim listesi (dropdown) bulunur.
                  Satış Fiyatı ve Stok yalnız sayı kabul eder (boş = değişmez).
                  <br />Stok farkları, fark kadar sayım/düzeltme hareketi olarak işlenir (geçmiş korunur).
                  <br />Önce önizleme gösterilir; onaylamadan hiçbir kayıt değişmez.
                </div>
                <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="classify-upload-btn"
                  className="bg-cyan-700 hover:bg-cyan-600 rounded-sm text-xs uppercase tracking-wider">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" /> Excel'den Güncelle</>}
                </Button>
                <input ref={fileRef} type="file" accept=".xlsx" className="hidden"
                  data-testid="classify-file-input" onChange={(e) => doPreview(e.target.files?.[0])} />
              </div>
            </div>
          )}

          {preview && (
            <div className="space-y-4" data-testid="classify-preview">
              <div className="grid grid-cols-3 gap-2">
                {COUNT_LABELS.map(([key, label, cls]) => (
                  <div key={key} className={`rounded-sm border bg-slate-950/40 px-2 py-1.5 text-center ${cls}`} data-testid={`classify-count-${key}`}>
                    <div className="text-lg font-bold font-mono">{preview[key] ?? 0}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
                  </div>
                ))}
              </div>

              <div className="text-xs text-slate-400">
                <span className="text-cyan-300 font-mono font-semibold" data-testid="classify-degisecek">{degisecekUrun}</span> üründe değişiklik uygulanacak
                {preview.changes_truncated && <span className="text-slate-500"> (ilk 500 gösteriliyor)</span>}
              </div>

              <div className="border border-slate-800 rounded-sm overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-slate-800">
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ürün</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Alan</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Eski</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Yeni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(preview.changes || []).map((c, i) => (
                        <TableRow key={i} className="border-slate-800" data-testid={`classify-change-row-${i}`}>
                          <TableCell className="text-xs text-slate-300">{c.name}</TableCell>
                          <TableCell className="text-xs text-slate-500">{FIELD_LABELS[c.field] || "Sınıflandırma"}</TableCell>
                          <TableCell className="text-xs text-slate-500">{c.old}</TableCell>
                          <TableCell className="text-xs text-cyan-300 font-semibold">{c.new}</TableCell>
                        </TableRow>
                      ))}
                      {!(preview.changes || []).length && (
                        <TableRow className="border-slate-800">
                          <TableCell colSpan={4} className="text-center text-xs text-slate-500 py-6">Değişiklik yok</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {!!(preview.error_rows || []).length && (
                <div className="rounded-sm border border-rose-900/50 bg-rose-950/20 p-3" data-testid="classify-errors">
                  <div className="text-[11px] font-semibold text-rose-300 mb-1">Hatalı satırlar ({preview.error_rows.length})</div>
                  <div className="max-h-28 overflow-y-auto space-y-0.5">
                    {preview.error_rows.map((e, i) => (
                      <div key={i} className="text-[10px] font-mono text-rose-200/80">Satır {e.row}: {e.reason}</div>
                    ))}
                  </div>
                </div>
              )}
              {!!(preview.unmatched_rows || []).length && (
                <div className="rounded-sm border border-orange-900/50 bg-orange-950/20 p-3" data-testid="classify-unmatched">
                  <div className="text-[11px] font-semibold text-orange-300 mb-1">Eşleşmeyen satırlar ({preview.unmatched_rows.length})</div>
                  <div className="max-h-28 overflow-y-auto space-y-0.5">
                    {preview.unmatched_rows.map((e, i) => (
                      <div key={i} className="text-[10px] font-mono text-orange-200/80">
                        Satır {e.row}: {e.product_id || e.source_id || e.sku_kod || "(boş)"}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-1">
                <Button variant="outline" onClick={() => { setPreview(null); setFile(null); }} disabled={busy}
                  data-testid="classify-back-btn"
                  className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm text-xs">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Geri
                </Button>
                <Button onClick={doApply} disabled={busy || !degisecekUrun} data-testid="classify-apply-btn"
                  className="bg-emerald-600 hover:bg-emerald-500 rounded-sm text-xs uppercase tracking-wider font-semibold">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> Onayla ve Uygula ({degisecekUrun})</>}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
